import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import {
  useScrollableCanvasNavigation, createScrollableCanvasPan, updateScrollableCanvasPan,
  type ScrollableCanvasPan,
} from "../../../src/components/editorViewport/useScrollableCanvasNavigation";

/** Local navigation enlarges the viewport with its content while leaving the floor plan unchanged. */
export function C1_ButtonEditorViewport({ children, onViewportChange }: {
  children: ReactNode;
  onViewportChange: () => void;
}) {
  const viewport = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const pan = useRef<ScrollableCanvasPan>();
  const suppressClick = useRef(false);
  const [zoom, setZoom] = useState(1);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useScrollableCanvasNavigation({ viewportRef: viewport, canvasRef: canvas, zoom, setZoom, minZoom: 0.75, maxZoom: 2, chainScroll: true });

  useLayoutEffect(() => {
    const element = content.current;
    if (!element) return;
    const measure = () => {
      const width = element.offsetWidth, height = element.offsetHeight;
      setSize(previous => previous.width === width && previous.height === height ? previous : { width, height });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useLayoutEffect(onViewportChange, [zoom, size.width, size.height, onViewportChange]);

  return <div ref={viewport} className="C1_buttonEditorViewport" data-canvas-navigation="ignore"
    data-canvas-navigation-viewport="" data-testid="c1-button-editor-viewport" data-zoom={zoom}
    style={{ height: size.height ? size.height * zoom : undefined }}
    onWheel={event => event.stopPropagation()}
    onScroll={onViewportChange}
    onPointerDownCapture={event => {
      suppressClick.current = false;
      if (event.button !== 1 && !(event.button === 0 && event.altKey)) return;
      if ((event.target as Element).closest("input, textarea, select, button, [contenteditable='true']")) return;
      event.preventDefault();
      event.stopPropagation();
      pan.current = createScrollableCanvasPan(event.currentTarget, event.pointerId, event.clientX, event.clientY);
      suppressClick.current = event.button === 0;
      event.currentTarget.setPointerCapture(event.pointerId);
    }}
    onPointerMove={event => {
      if (!pan.current || pan.current.pointerId !== event.pointerId) return;
      event.preventDefault();
      event.stopPropagation();
      updateScrollableCanvasPan(event.currentTarget, pan.current, event.clientX, event.clientY);
    }}
    onPointerUp={event => {
      if (pan.current?.pointerId !== event.pointerId) return;
      pan.current = undefined;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    }}
    onPointerCancel={() => { pan.current = undefined; suppressClick.current = false; }}
    onLostPointerCapture={() => { pan.current = undefined; }}
    onClickCapture={event => {
      if (!suppressClick.current) return;
      suppressClick.current = false;
      event.preventDefault();
      event.stopPropagation();
    }}>
    <div ref={canvas} className="C1_buttonEditorViewportCanvas"
      style={{ width: size.width ? size.width * zoom : undefined, height: size.height ? size.height * zoom : undefined }}>
      <div ref={content} className="C1_buttonEditorViewportContent" style={{ transform: `scale(${zoom})` }}>{children}</div>
    </div>
  </div>;
}
